import React, { useState } from 'react';
import { Eye, EyeOff } from 'feather-icons-react/build/IconComponents';

/**
 * InputComponent — Shopify/shadcn-style reusable input for the entire admin panel.
 *
 * Props:
 *   id           – HTML id
 *   name         – field name
 *   type         – input type (default: 'text')
 *   value        – controlled value
 *   onChange     – change handler
 *   placeholder  – placeholder text
 *   required     – shows a * next to the label
 *   className    – extra classes merged onto the input element
 *   label        – label text rendered above the input
 *   tabIndex     – HTML tabIndex
 *   onKeyDown    – keyDown handler
 *   error        – validation error string shown below input (red)
 *   helpText     – helper text shown below input (gray)
 *   prefix       – JSX element / icon rendered on the LEFT inside the input
 *   suffix       – JSX element / icon rendered on the RIGHT inside the input
 *   size         – 'sm' | 'md' (default)
 *   disabled     – disables the input
 */
const InputComponent = ({
  id,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  className = '',
  label,
  tabIndex,
  onKeyDown,
  error = '',
  helpText = '',
  prefix,
  suffix,
  size = 'md',
  disabled = false,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const hasValue = value !== undefined && value !== null && value.toString().trim() !== '';

  // ── Size Styles ───────────────────────────────────────────────────────
  const sizeClasses = {
    sm: 'px-2.5 py-1 text-xs h-8 placeholder:text-xs',
    md: 'px-3 py-1.5 text-[13px] h-9 placeholder:text-[13px]',
  };

  const appliedSize = sizeClasses[size] || sizeClasses.md;

  // ── Prefix/Suffix padding adjustments ────────────────────────────────
  const hasPrefixAdornment = !!prefix;
  const hasSuffixAdornment = !!suffix || isPassword;

  const paddingLeft = hasPrefixAdornment ? '!pl-9' : '';
  const paddingRight = hasSuffixAdornment ? '!pr-9' : '';

  // ── Input classes ─────────────────────────────────────────────────────
  const inputClasses = [
    'w-full border rounded-lg outline-none transition-all duration-150',
    'text-[var(--primary-text-color)] placeholder:text-[var(--table-text-color)] placeholder:font-normal',
    'bg-[var(--secondary-bg-color)]',
    'border-[var(--border-color)]',
    'hover:border-[var(--focus-border-color)]',
    'focus:border-[var(--focus-border-color)] focus:ring-0 focus:bg-[var(--secondary-bg-color)]',
    error ? 'border-[var(--color-danger)] focus:ring-0 focus:border-[var(--color-danger)]' : '',
    disabled ? 'opacity-60 cursor-not-allowed bg-[var(--focus-tab-color)]' : '',
    hasValue && !disabled ? 'bg-[var(--secondary-bg-color)]' : '',
    appliedSize,
    paddingLeft,
    paddingRight,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className='w-full flex flex-col gap-1 text-left'>
      {label && (
        <label
          htmlFor={id}
          className='text-[var(--primary-text-color)] font-medium text-[13px] tracking-tight'
          style={{ marginBottom: 0 }}
        >
          {label}
          {required && <span className='text-[var(--color-danger)] ml-0.5'>*</span>}
        </label>
      )}

      <div className='relative flex items-center w-full'>
        {/* Prefix adornment */}
        {prefix && (
          <div className='absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-[var(--table-text-color)]'>
            {prefix}
          </div>
        )}

        <input
          id={id}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />

        {/* Suffix adornment (non-password) */}
        {suffix && !isPassword && (
          <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-[var(--table-text-color)]'>
            {suffix}
          </div>
        )}

        {/* Password toggle */}
        {isPassword && (
          <button
            type='button'
            tabIndex={-1}
            onClick={() => setShowPassword(!showPassword)}
            className='absolute right-3 top-1/2 -translate-y-1/2 text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] focus:outline-none transition-colors'
          >
            {showPassword ? <EyeOff className='w-4 h-4' /> : <Eye className='w-4 h-4' />}
          </button>
        )}
      </div>

      {/* Help text */}
      {helpText && !error && (
        <p className='text-xs text-[var(--table-text-color)] mt-0.5'>{helpText}</p>
      )}

      {/* Error text */}
      {error && (
        <p className='text-xs text-[var(--color-danger)] font-medium mt-0.5'>{error}</p>
      )}
    </div>
  );
};

export default InputComponent;